import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { Radio, Users, ArrowLeft, Play, Share2, Maximize2 } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { api, RANKS } from "../lib/api";
import { createLiveSocket, voteLink } from "../lib/liveSocket";
import { sfx } from "../lib/sound";

const VoteBar = ({ label, count, total, pct, highlight, dim }) => (
  <div
    className={`relative overflow-hidden rounded-2xl border p-4 transition-all ${
      highlight ? "border-[#38ef7d] scale-[1.02]" : dim ? "border-white/10 opacity-50" : "border-white/15"
    }`}
  >
    <div
      className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#f093fb]/40 to-[#f5576c]/40 transition-all duration-300"
      style={{ width: `${pct}%` }}
    />
    <div className="relative flex items-center justify-between gap-3">
      <span className="font-semibold text-white">{label}</span>
      <span className="font-display text-lg font-black text-white">{pct}%</span>
    </div>
    <div className="relative mt-1 text-xs text-white/50">{count} / {total}</div>
  </div>
);

export const LiveHostScreen = ({ onExit }) => {
  const { t, lang, dict } = useLang();
  const [phase, setPhase] = useState("setup");
  const [domains, setDomains] = useState([]);
  const [domaine, setDomaine] = useState(null);
  const [room, setRoom] = useState(null);
  const [viewers, setViewers] = useState(0);
  const [vote, setVote] = useState(null);
  const [counts, setCounts] = useState([0, 0]);
  const [reveal, setReveal] = useState(null);
  const [result, setResult] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [copied, setCopied] = useState(false);
  const [reactions, setReactions] = useState([]);
  const [obs, setObs] = useState(false);
  const socketRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    api.getDomains(lang).then(setDomains).catch(() => {});
  }, [lang]);

  useEffect(() => {
    const s = createLiveSocket();
    socketRef.current = s;
    s.on("viewers", (d) => setViewers(d.count));
    s.on("vote_open", (d) => {
      setReveal(null);
      setVote(d);
      setCounts(d.choix.map(() => 0));
      setPhase("voting");
      setRemaining(d.duration);
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
      try { sfx.voteOpen(); } catch (e) {}
    });
    s.on("tally", (d) => setCounts(d.counts));
    s.on("vote_result", (d) => {
      setReveal(d);
      setPhase("reveal");
      if (timerRef.current) clearInterval(timerRef.current);
      try { sfx.reveal(); } catch (e) {}
    });
    s.on("game_over", (d) => { setResult(d.result); setPhase("over"); try { sfx.victory(); } catch (e) {} });
    s.on("reaction", (d) => {
      const id = Date.now() + Math.random();
      setReactions((prev) => [...prev, { id, emoji: d.emoji, x: 8 + Math.random() * 84 }]);
      setTimeout(() => setReactions((prev) => prev.filter((r) => r.id !== id)), 3000);
    });
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      s.disconnect();
    };
  }, []);

  const createRoom = () => {
    if (!domaine) return;
    const pseudo = localStorage.getItem("de_pseudo") || "Le streamer";
    socketRef.current.emit("host_create", { domaine, lang, pseudo }, (res) => {
      if (res?.ok) { setRoom(res); setPhase("lobby"); }
    });
  };

  const startGame = () => socketRef.current.emit("host_start", {});

  const toggleObs = async () => {
    try {
      if (!document.fullscreenElement) await document.documentElement.requestFullscreen();
      else if (document.fullscreenElement) await document.exitFullscreen();
    } catch (e) {}
    setObs((o) => !o);
  };

  const shareResult = async () => {
    if (!result?.id) return;
    const url = api.challengeUrl(result.id);
    const n = result.live_voters || 0;
    const text = t("liveShareChallengeText", { n });
    try {
      if (navigator.share) await navigator.share({ text, url });
      else { await navigator.clipboard.writeText(`${text} ${url}`); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    } catch (e) {}
  };

  const copyLink = async () => {
    if (!result?.id) return;
    try {
      await navigator.clipboard.writeText(api.challengeUrl(result.id));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {}
  };

  const total = counts.reduce((a, b) => a + b, 0);
  const pct = (i) => (total > 0 ? Math.round((counts[i] / total) * 100) : 0);
  const link = room ? voteLink(room.code) : "";
  const rankColor = result ? RANKS[result.rank] || RANKS.anonyme : null;

  return (
    <div className="relative flex min-h-full flex-col px-5 py-6" data-testid="live-host-screen">
      {/* Réactions flottantes du public */}
      <div className="pointer-events-none absolute inset-0 z-40 overflow-hidden">
        <AnimatePresence>
          {reactions.map((r) => (
            <motion.div key={r.id}
              initial={{ opacity: 0, y: 0, scale: 0.5 }}
              animate={{ opacity: 1, y: -420, scale: 1.4 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 3, ease: "easeOut" }}
              style={{ left: `${r.x}%`, bottom: 40 }}
              className="absolute text-4xl">
              {r.emoji}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {!obs && (
      <div className="flex items-center gap-3">
        <button onClick={onExit} data-testid="live-exit-button"
          className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white/70">
          <ArrowLeft size={18} />
        </button>
        <div className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-widest text-[#f5576c]">
          <span className="h-2 w-2 animate-pulse rounded-full bg-[#f5576c]" /> {t("live")}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {room && (phase === "voting" || phase === "reveal" || phase === "lobby") && (
            <button onClick={toggleObs} data-testid="live-obs-toggle"
              className="flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/80">
              <Maximize2 size={14} className="text-[#f093fb]" /> {t("liveObs")}
            </button>
          )}
          {room && (
            <div className="flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1.5 text-sm font-semibold text-white" data-testid="live-viewers">
              <Users size={15} className="text-[#f093fb]" /> {viewers}
            </div>
          )}
        </div>
      </div>
      )}
      {obs && (
        <button onClick={toggleObs} data-testid="live-obs-exit"
          className="absolute right-4 top-4 z-50 flex items-center gap-1.5 rounded-full border border-white/15 bg-black/40 px-3 py-1.5 text-xs font-semibold text-white/70">
          <Maximize2 size={14} /> {viewers} 👁
        </button>
      )}

      {/* SETUP */}
      {phase === "setup" && (
        <div className="mt-6">
          <h1 className="font-display text-3xl font-black text-white">{t("liveHostTitle")}</h1>
          <p className="mt-2 text-sm text-white/60">{t("liveHostDesc")}</p>
          <p className="mt-6 mb-3 text-sm font-semibold text-white/80">{t("liveChooseDomain")}</p>
          <div className="grid grid-cols-2 gap-3">
            {domains.map((d) => (
              <button key={d.id} onClick={() => setDomaine(d.id)}
                data-testid={`live-domain-${d.id}`}
                className={`rounded-2xl border p-4 text-left transition-all ${
                  domaine === d.id ? "border-[#f093fb] bg-[#f093fb]/10 scale-[1.02]" : "border-white/12 bg-white/5"
                }`}>
                <div className="font-display text-lg font-bold text-white">{d.nom}</div>
                <div className="mt-1 text-xs text-white/50">{d.tagline}</div>
              </button>
            ))}
          </div>
          <motion.button whileTap={{ scale: 0.96 }} disabled={!domaine} onClick={createRoom}
            data-testid="live-create-button"
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#f093fb] to-[#f5576c] py-5 font-display text-lg font-extrabold uppercase text-white disabled:opacity-40">
            <Radio size={20} /> {t("liveCreateRoom")}
          </motion.button>
        </div>
      )}

      {/* LOBBY */}
      {phase === "lobby" && room && (
        <div className="mt-6 text-center">
          <h1 className="font-display text-2xl font-black text-white">{t("liveShareTitle")}</h1>
          <div className="mx-auto mt-6 w-fit rounded-3xl bg-white p-4">
            <QRCodeSVG value={link} size={190} data-testid="live-qr" />
          </div>
          <p className="mt-4 text-sm text-white/60">{t("liveScanQr")}</p>
          <p className="mt-1 break-all font-semibold text-[#f093fb]">{link}</p>
          <div className="mt-5 inline-flex flex-col items-center rounded-2xl border border-white/12 bg-white/5 px-8 py-4">
            <span className="text-xs uppercase tracking-widest text-white/40">{t("liveRoomCode")}</span>
            <span className="font-display text-4xl font-black tracking-[0.3em] text-white" data-testid="live-code">{room.code}</span>
          </div>
          <p className="mt-5 text-sm text-white/60">
            <Users size={15} className="mr-1 inline text-[#f093fb]" /> {viewers} {t("liveViewers")}
          </p>
          <motion.button whileTap={{ scale: 0.96 }} onClick={startGame}
            data-testid="live-start-button"
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#38ef7d] to-[#11998e] py-5 font-display text-lg font-extrabold uppercase text-white">
            <Play size={20} fill="white" /> {t("liveStart")}
          </motion.button>
        </div>
      )}

      {/* VOTING / REVEAL */}
      {(phase === "voting" || phase === "reveal") && (vote || reveal) && (
        <div className={obs ? "mt-10 px-4" : "mt-6"} data-testid="live-vote-panel">
          <div className="flex items-center justify-between text-sm text-white/50">
            <span>{t("liveQuestion")} {(vote?.index ?? reveal?.index) + 1}/{vote?.total ?? room?.total}</span>
            {phase === "voting" && <span className={`font-display font-black text-[#f5576c] ${obs ? "text-4xl" : "text-2xl"}`} data-testid="live-countdown">{remaining}s</span>}
            {phase === "reveal" && <span className="font-semibold text-[#38ef7d]">{t("liveMajorityChose")}</span>}
          </div>
          {vote?.rare && (
            <span className="mt-3 inline-block rounded-full bg-gradient-to-r from-[#ffd700] to-[#f5576c] px-3 py-1 font-display text-[11px] font-bold uppercase tracking-widest text-black" data-testid="live-rare-badge">
              {vote.rarete === "legendaire" ? t("rareLegendary") : t("rareEpic")}
            </span>
          )}
          <p className={`mt-3 font-display font-bold leading-snug text-white ${obs ? "text-4xl" : "text-xl"}`} data-testid="live-question-text">
            {vote?.texte || (reveal && room && "")}
          </p>
          <div className="mt-5 space-y-3">
            {(vote?.choix || []).map((label, i) => (
              <VoteBar key={i} label={label} count={counts[i] || 0} total={total} pct={pct(i)}
                highlight={phase === "reveal" && reveal?.chosen === i}
                dim={phase === "reveal" && reveal?.chosen !== i} />
            ))}
          </div>
          {phase === "reveal" && reveal && (
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className="mt-4 rounded-2xl border border-[#38ef7d]/30 bg-[#38ef7d]/10 p-3 text-center text-sm text-[#38ef7d]">
              {reveal.feedback}
            </motion.p>
          )}
        </div>
      )}

      {/* OVER */}
      {phase === "over" && result && (
        <div className="mt-6 text-center" data-testid="live-over">
          <p className="text-sm uppercase tracking-widest text-white/50">{t("liveFinished")}</p>
          {result.live_voters > 0 && (
            <p className="mt-1 text-sm font-semibold text-[#f5576c]" data-testid="live-voters-count">
              {t("liveVotersDecided", { n: result.live_voters })}
            </p>
          )}
          <div className="mt-5 font-display text-5xl font-black uppercase"
            style={{ color: rankColor?.c1 }}>{dict.ranks?.[result.rank] || result.rank}</div>
          <div className="mt-3 font-display text-4xl font-black text-white" data-testid="live-final-score">
            {(result.score || 0).toLocaleString()}
          </div>
          <p className="text-xs uppercase tracking-widest text-white/40">points</p>

          {/* Lien de défi collectif — convertir les spectateurs en joueurs */}
          <div className="mt-7 rounded-3xl border border-[#f093fb]/40 bg-gradient-to-b from-[#f093fb]/10 to-transparent p-5">
            <p className="font-display text-lg font-extrabold leading-snug text-white" data-testid="live-beat-destiny">
              {t("liveBeatDestiny", { n: result.live_voters || 0 })}
            </p>
            <p className="mt-1 text-xs text-white/50">{t("liveConvertHint")}</p>
            <div className="mx-auto mt-4 w-fit rounded-2xl bg-white p-3">
              <QRCodeSVG value={api.challengeUrl(result.id)} size={150} data-testid="live-challenge-qr" />
            </div>
            <p className="mt-3 break-all text-xs font-semibold text-[#f093fb]" data-testid="live-challenge-link">
              {api.challengeUrl(result.id)}
            </p>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <button onClick={copyLink} data-testid="live-copy-link"
                className="rounded-2xl border border-white/15 bg-white/5 py-3 text-sm font-semibold text-white/85">
                {copied ? t("liveLinkCopied") : t("liveCopyLink")}
              </button>
              <button onClick={shareResult} data-testid="live-share-button"
                className="flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#f093fb] to-[#f5576c] py-3 text-sm font-bold text-white">
                <Share2 size={16} /> {t("share")}
              </button>
            </div>
          </div>

          <button onClick={onExit} data-testid="live-home-button"
            className="mt-4 w-full rounded-2xl border border-white/15 bg-white/5 py-4 text-sm font-semibold text-white/85">
            {t("liveBackHome") || "Menu"}
          </button>
        </div>
      )}
    </div>
  );
};
